// The Jobs tab: Active / Completed, PM and search filters, status chips, and the list of job cards.
import type { FilterKey, Item, JobGroup } from '@shared/index';
import { buildJobGroups, filters, groupDoneMap, matchesSearch, money } from '@shared/index';
import { jobItems, store, today, ui } from '../store';
import { registerView, render } from '../render';
import { setPm } from '../nav';
import { $, el, pmSeg, segBind } from '../ui';
import { card } from './card';

export interface JobsFilter { pm: string; q: string; view: 'active' | 'done'; filter: FilterKey }

export interface JobsModel {
  nAct: number;
  nDone: number;
  /** Items in the current view (Active or Completed), before the status filter. */
  base: Item[];
  /** Status chips to show: [key, label, count]. Empty chips are hidden unless selected. */
  chips: [FilterKey, string, number][];
  rows: JobGroup[];
  nVars: number;
  left: number;
}

/** Everything the Jobs tab shows, worked out without touching the page. */
export function jobsModel(items: Item[], f: JobsFilter, now: string): JobsModel {
  const vis = items.filter((j) => (!f.pm || j.pm === f.pm) && matchesSearch(j, f.q, true));
  // Group variations first so a job and its variations move to Completed together.
  const groupDone = groupDoneMap(vis);
  const nDone = vis.filter((j) => groupDone.get(j.quote)).length;
  const base = vis.filter((j) => (f.view === 'done') === !!groupDone.get(j.quote));
  const F = filters(now);
  const chips: [FilterKey, string, number][] = [];
  F.forEach(([k, label, fn]) => {
    const n = base.filter(fn).length;
    if (k && !n && f.filter !== k) return;
    chips.push([k, label, n]);
  });
  const fn = (F.find(([k]) => k === f.filter) || F[0])[2];
  const rows = buildJobGroups(base, f.view, fn, now);
  return {
    nAct: vis.length - nDone,
    nDone,
    base,
    chips,
    rows,
    nVars: rows.reduce((t, g) => t + g.vars.length, 0),
    left: rows.reduce((t, g) => t + Number(g.main.remaining || 0) + g.vars.reduce((u, v) => u + Number(v.remaining || 0), 0), 0),
  };
}

/** "12 jobs (+3 variations inside) · $450,000 left to claim" */
export function countLine(m: Pick<JobsModel, 'rows' | 'nVars' | 'left'>): string {
  const n = m.rows.length;
  return n + (n === 1 ? ' job' : ' jobs') + (m.nVars ? ' (+' + m.nVars + ' variations inside)' : '') + ' · ' + money(m.left) + ' left to claim';
}

function renderChips(chips: JobsModel['chips']) {
  const box = $('chips');
  box.replaceChildren();
  chips.forEach(([k, label, n]) => {
    const b = el('button', 'chip' + (k === 'crane' || k === 'nohand' ? ' alert' : ''));
    b.type = 'button';
    b.setAttribute('aria-pressed', String(ui.filter === k));
    b.append(label, el('b', undefined, n));
    b.onclick = () => { ui.filter = k; render(['jobs']); };
    box.append(b);
  });
}

/** Controls that other screens can change (openJob sets the view, PM and search), so redraw them every time. */
function syncControls() {
  document.querySelectorAll<HTMLButtonElement>('#doneSeg button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.v === ui.view)));
  pmSeg($('pmSeg'), ui.pm, setPm);
  const q = $<HTMLInputElement>('q');
  if (q.value !== ui.q) q.value = ui.q;
}

function empty(text: string): HTMLElement {
  return el('div', 'empty', text);
}

function renderJobs() {
  syncControls();
  if (!store.loaded) return;
  const m = jobsModel(jobItems(), ui, today());
  $('nDone').textContent = '(' + m.nDone + ')';
  $('nAct').textContent = '(' + m.nAct + ')';
  renderChips(m.chips);
  $('count').textContent = countLine(m);
  const list = $('list');
  list.replaceChildren();
  if (!store.jobs.length) { list.append(empty('No jobs yet. Jobs appear here once they are loaded from the Structural Jobs sheet.')); return; }
  if (!m.rows.length) {
    list.append(empty(ui.view === 'done' && !m.base.length
      ? 'No completed jobs yet. Set a job’s status to Complete or enter a completed date and it moves here.'
      : 'No jobs match this filter.'));
    return;
  }
  // If a search or filter matches only a variation, open its parent so the match is visible.
  const narrowed = !!(ui.filter || ui.q.trim());
  m.rows.forEach((g) => list.append(card(g.main, g.vars, false, narrowed && !g.mainHit && g.varHit)));
}

export function initJobs() {
  segBind('doneSeg', 'v', (v) => { ui.view = v === 'done' ? 'done' : 'active'; ui.filter = ''; render(['jobs']); });
  $<HTMLInputElement>('q').addEventListener('input', (e) => { ui.q = (e.target as HTMLInputElement).value; render(['jobs']); });
  registerView('jobs', renderJobs);
}
